import { apiClient } from '../lib/apiClient';

/**
 * weekly-report-search 用の共通パラメータ組み立て・取得ユーティリティ
 *  - 各画面の検索ロジックを共通化
 */

export type SimpleOption = {
  id: string;
  value: string;
  user_id?: string | null;
  legacy?: boolean;
};
export type ReportTypeLike = { id: number | string; label: string };

export type WeeklyReportCreatorOption = SimpleOption & {
  department_id?: string;
  user_id?: string | null;
  legacy?: boolean;
};

export interface WeeklyReportCreatorFetchArgs {
  startDate: string;
  endDate: string;
  selectedDepartmentIds?: Array<string | number>;
  selectedTeamIds?: Array<string | number>;
  /** Overwrite Old では下書き(temporary_flg=1)の発行者も候補に含める。 */
  includeTemporary?: boolean;
  userDep?: string;
  userTeamId?: string | number;
  signal?: AbortSignal;
}

export interface WeeklyReportCreatorSnapshot {
  options: WeeklyReportCreatorOption[];
  cursor: number | null;
}

export interface WeeklyReportCreatorDelta {
  nextCursor: number;
  resetRequired: boolean;
  upserts: WeeklyReportCreatorOption[];
  deletedIds: string[];
}

export interface WeeklyReportCreatorPage {
  items: WeeklyReportCreatorOption[];
  nextPageCursor: string | null;
  hasMore: boolean;
  syncCursor: number | null;
}

export interface WeeklySearchBuildArgs {
  // wr_id が指定された場合は、それ以外の条件は無視する
  wrId?: string | number;
  // 画面側で YYYY-MM-DD に整形済みの文字列を渡す（タイムゾーン差異を避けるため）
  startDate?: string;
  endDate?: string;
  freeWord?: string;
  searchTitleBody?: boolean;
  searchTags?: boolean;

  // 週報分類: 画面の選択は id 配列、master から label を解決して送る
  selectedReportTypeIds?: string[];
  allReportTypes?: ReportTypeLike[];

  // チーム / 課: API には id を送る
  selectedTeamIds?: string[];
  selectedDepartmentIds?: Array<string | number>;

  // 記入者: id or label が入ってくる前提。options から label を解決（あれば）
  selectedCreateByValues?: string[];
  // URL復元など、候補取得前でも user_id と確定している値
  selectedCreateByUserIds?: string[];
  createByOptions?: SimpleOption[];

  // WeeklyReport/SearchArea など一部画面では 0 を必ず付与する
  appendTemporaryFlg0?: boolean;
}

/** 内部ユーティリティ: ラベル解決（id または value に一致すれば value を返す） */
function resolveCreateByLabel(options: SimpleOption[] | undefined, idOrName: string): string {
  if (!options || options.length === 0) return idOrName;
  const found = options.find(o => String(o.id) === String(idOrName) || String(o.value) === String(idOrName));
  return found ? found.value : idOrName;
}

/** 内部ユーティリティ: report_types のラベル解決（id -> label） */
function resolveReportTypeLabel(all: ReportTypeLike[] | undefined, id: string): string {
  if (!all || all.length === 0) return id;
  const found = all.find(r => String(r.id) === String(id));
  return found?.label ?? id;
}

/**
 * 検索パラメータを URLSearchParams に組み立てる
 */
export function buildWeeklySearchParams(args: WeeklySearchBuildArgs): URLSearchParams {
  const {
    wrId,
    startDate,
    endDate,
    freeWord,
    searchTitleBody,
    searchTags,
    selectedReportTypeIds = [],
    allReportTypes = [],
    selectedTeamIds = [],
    selectedDepartmentIds = [],
    selectedCreateByValues = [],
    selectedCreateByUserIds = [],
    createByOptions = [],
    appendTemporaryFlg0 = false,
  } = args;

  const params = new URLSearchParams();

  if (wrId != null && String(wrId).trim() !== '') {
    params.set('wr_id', String(wrId).trim());
    return params;
  }

  if (startDate) params.append('start_date', startDate);
  if (endDate) params.append('end_date', endDate);
  const freeWordTrimmed = typeof freeWord === 'string' ? freeWord.trim() : '';
  const hasFreeWord = freeWordTrimmed.length > 0;
  const hasSearchFlags =
    typeof searchTitleBody === 'boolean' || typeof searchTags === 'boolean';
  const useTitleBody =
    typeof searchTitleBody === 'boolean' ? searchTitleBody : true;
  const useTags = typeof searchTags === 'boolean' ? searchTags : false;

  if (hasFreeWord && (useTitleBody || useTags)) {
    params.append('free_word', freeWordTrimmed);
  }
  // フリーワードが空でも、明示フラグは送る（検索対象の保持用）
  if (hasSearchFlags) {
    params.append('search_title_body', useTitleBody ? '1' : '0');
    params.append('search_tags', useTags ? '1' : '0');
  }

  // report_types は label を送る
  selectedReportTypeIds.forEach(id => {
    params.append('report_types', resolveReportTypeLabel(allReportTypes, id));
  });

  // teams / departments は id を送る
  selectedTeamIds.forEach(id => params.append('teams', String(id)));
  selectedDepartmentIds.forEach(id => params.append('departments', String(id)));

  // 新しい候補は create_by_user_id、旧/legacy候補は従来どおり氏名を送る。
  // 両方が指定された場合、バックエンドは OR 条件として扱う。
  const explicitUserIds = new Set(
    selectedCreateByUserIds.map((value) => String(value ?? '').trim()).filter(Boolean)
  );
  explicitUserIds.forEach((userId) => params.append('create_by_user_ids', userId));

  selectedCreateByValues.forEach(idOrName => {
    if (explicitUserIds.has(String(idOrName).trim())) return;
    const found = createByOptions.find(
      o => String(o.id) === String(idOrName)
    );
    const userId = String(found?.user_id ?? '').trim();
    if (found && !found.legacy && userId) {
      if (!explicitUserIds.has(userId)) {
        params.append('create_by_user_ids', userId);
      }
      return;
    }
    if (found?.legacy) {
      params.append('legacy_create_by', found.value);
      return;
    }
    // 表示名で渡された旧URL/旧キャッシュ値は user_id に推測変換しない。
    // 同姓同名では候補を一意に決められないため、従来の氏名条件を維持する。
    params.append('create_by', resolveCreateByLabel(createByOptions, idOrName));
  });

  if (appendTemporaryFlg0) {
    params.set('temporary_flg', '0');
  }

  return params;
}

/**
 * weekly-report-search を実行して結果配列を返す
 */
export async function fetchWeeklyReports<T = any>(params: URLSearchParams, signal?: AbortSignal): Promise<T[]> {
  const resp = await apiClient.get<T[]>('/weekly-report-search', { params, signal });
  return Array.isArray(resp.data) ? resp.data : [];
}

/**
 * 期間・課・公開範囲に該当する発行者候補を取得する。
 *
 * 週報本体の検索結果から候補を逆算すると検索APIの件数上限に影響されるため、
 * 発行者専用APIを利用する。レスポンスの型揺れはここで吸収し、画面には常に
 * `id` / `value` を持つ候補だけを返す。
 */
function buildCreatorParams(args: WeeklyReportCreatorFetchArgs): URLSearchParams {
  const params = new URLSearchParams();
  params.set('start_date', args.startDate);
  params.set('end_date', args.endDate);

  (args.selectedDepartmentIds ?? []).forEach((id) => {
    const value = String(id ?? '').trim();
    if (value) params.append('departments', value);
  });

  (args.selectedTeamIds ?? []).forEach((id) => {
    const value = String(id ?? '').trim();
    if (value) params.append('teams', value);
  });

  if (args.includeTemporary) params.set('include_temporary', '1');

  const userDep = String(args.userDep ?? '').trim();
  if (userDep) params.set('USER_DEP', userDep);
  const userTeamId = String(args.userTeamId ?? '').trim();
  if (userTeamId) params.set('USER_TEAM_ID', userTeamId);
  return params;
}

function normalizeCreatorOptions(rows: unknown): WeeklyReportCreatorOption[] {
  if (!Array.isArray(rows)) return [];

  return rows
    .map((row: any): WeeklyReportCreatorOption | null => {
      const id = String(
        row?.id ?? row?.user_id ?? row?.create_by_user_id ?? ''
      ).trim();
      const value = String(
        row?.value ?? row?.label ?? row?.create_by ?? ''
      ).trim();
      if (!id || !value) return null;

      const departmentIdRaw =
        row?.department_id ?? row?.departmentId ?? row?.department;
      const departmentId =
        departmentIdRaw == null || String(departmentIdRaw).trim() === ''
          ? undefined
          : String(departmentIdRaw).trim();
      const userIdRaw = row?.user_id ?? row?.create_by_user_id;
      const userId =
        userIdRaw == null || String(userIdRaw).trim() === ''
          ? null
          : String(userIdRaw).trim();

      return {
        id,
        value,
        department_id: departmentId,
        user_id: userId,
        legacy: Boolean(row?.legacy),
      };
    })
    .filter((item): item is WeeklyReportCreatorOption => item != null);
}

// 発行者候補APIも teams / departments をクエリに載せるため、チーム全選択などで
// WAFのクエリ長上限(2KB)を超える。カーソル・検索語などの追加分を見込んで余白を残し、
// 上限に収まるバッチへ分割して取得し、結果を統合する。
const CREATOR_QUERY_RESERVE = 400;

const buildCreatorParamBatches = (
  args: WeeklyReportCreatorFetchArgs
): URLSearchParams[] =>
  splitParamsByQueryLength(
    buildCreatorParams(args),
    MAX_QUERY_LENGTH - CREATOR_QUERY_RESERVE
  );

const mergeCreatorOptionsById = (
  lists: WeeklyReportCreatorOption[][]
): WeeklyReportCreatorOption[] => {
  const merged = new Map<string, WeeklyReportCreatorOption>();
  lists.flat().forEach((option) => {
    if (!merged.has(option.id)) merged.set(option.id, option);
  });
  return Array.from(merged.values());
};

// 分割リクエスト全体（検索・候補取得・複数の期間チャンクをまたぐ）の同時実行数の上限。
// 全選択などで分割数が増えても、APIとDB接続への同時負荷が際限なく増えないようにする。
const MAX_BATCH_REQUESTS_IN_FLIGHT = 6;
let batchRequestsInFlight = 0;
const batchSlotWaiters: Array<() => void> = [];

const withBatchSlot = async <T>(task: () => Promise<T>): Promise<T> => {
  if (batchRequestsInFlight < MAX_BATCH_REQUESTS_IN_FLIGHT) {
    batchRequestsInFlight += 1;
  } else {
    // 解放時に枠がそのまま引き継がれる（待機側では加算しない）
    await new Promise<void>((resolve) => batchSlotWaiters.push(resolve));
  }
  try {
    return await task();
  } finally {
    const next = batchSlotWaiters.shift();
    if (next) next();
    else batchRequestsInFlight -= 1;
  }
};

const settleAllOrThrow = async <T>(
  tasks: Array<() => Promise<T>>
): Promise<T[]> => {
  const settled = await runWithConcurrency(
    tasks.map((task) => () => withBatchSlot(task)),
    BATCH_FETCH_CONCURRENCY
  );
  return settled.map((item) => {
    // 一部のバッチだけの候補を完全な候補として返さない
    if (item.status === 'rejected') throw item.reason;
    return item.value;
  });
};

async function fetchCreatorSnapshotWithParams(
  params: URLSearchParams,
  signal?: AbortSignal
): Promise<WeeklyReportCreatorSnapshot> {
  const resp = await apiClient.get<any[]>('/weekly-report-creators', {
    params,
    signal,
  });
  const cursorRaw = resp.headers?.['x-creator-sync-cursor'];
  const parsedCursor = Number(cursorRaw);
  return {
    options: normalizeCreatorOptions(resp.data),
    cursor: Number.isSafeInteger(parsedCursor) && parsedCursor >= 0 ? parsedCursor : null,
  };
}

export async function fetchWeeklyReportCreatorSnapshot(
  args: WeeklyReportCreatorFetchArgs,
  maxResults?: number
): Promise<WeeklyReportCreatorSnapshot> {
  const limit =
    maxResults != null && Number.isFinite(maxResults)
      ? Math.max(1, Math.floor(maxResults))
      : null;
  const batches = buildCreatorParamBatches(args);
  const snapshots = await settleAllOrThrow(
    batches.map((params) => () => {
      if (limit != null) params.set('max_results', String(limit));
      return fetchCreatorSnapshotWithParams(params, args.signal);
    })
  );
  if (snapshots.length === 1) return snapshots[0];

  const options = mergeCreatorOptionsById(snapshots.map((s) => s.options));
  // 同期カーソルは最も古いものを採用する（取得中の変更を差分同期で必ず拾うため）。
  // 1つでも欠けていれば、差分同期を前提にしない。
  const cursors = snapshots.map((s) => s.cursor);
  return {
    options: limit != null ? options.slice(0, limit) : options,
    cursor: cursors.some((c) => c == null)
      ? null
      : Math.min(...(cursors as number[])),
  };
}

async function fetchCreatorDeltaWithParams(
  params: URLSearchParams,
  cursor: number,
  signal?: AbortSignal
): Promise<WeeklyReportCreatorDelta> {
  params.set('cursor', String(cursor));
  const resp = await apiClient.get<any>('/weekly-report-creators-sync', {
    params,
    signal,
  });
  const data = resp.data ?? {};
  const nextCursor = Number(data.next_cursor);
  return {
    nextCursor:
      Number.isSafeInteger(nextCursor) && nextCursor >= 0 ? nextCursor : cursor,
    resetRequired: Boolean(data.reset_required),
    upserts: normalizeCreatorOptions(data.upserts),
    deletedIds: Array.isArray(data.deleted_ids)
      ? data.deleted_ids.map(String).filter((id: string) => id.trim().length > 0)
      : [],
  };
}

export async function fetchWeeklyReportCreatorDelta(
  args: WeeklyReportCreatorFetchArgs,
  cursor: number
): Promise<WeeklyReportCreatorDelta> {
  const batches = buildCreatorParamBatches(args);
  const deltas = await settleAllOrThrow(
    batches.map((params) => () =>
      fetchCreatorDeltaWithParams(params, cursor, args.signal)
    )
  );
  if (deltas.length === 1) return deltas[0];

  // 変更のあった発行者は全バッチで再評価されるため、ある発行者が「削除」になるのは
  // 全バッチが削除と判断し、かつどのバッチにも再登場しない場合だけ。
  const upserts = mergeCreatorOptionsById(deltas.map((d) => d.upserts));
  // サーバーは発行者IDを大文字小文字を区別せず比較するため、ここも同じ基準で判定する。
  const upsertedKeys = new Set(upserts.map((o) => o.id.toLocaleLowerCase()));
  const deletedKeysByBatch = deltas.map(
    (d) => new Set(d.deletedIds.map((id) => id.toLocaleLowerCase()))
  );
  const deletedIds = deltas[0].deletedIds.filter((id) => {
    const key = id.toLocaleLowerCase();
    return !upsertedKeys.has(key) && deletedKeysByBatch.every((s) => s.has(key));
  });
  return {
    nextCursor: Math.min(...deltas.map((d) => d.nextCursor)),
    resetRequired: deltas.some((d) => d.resetRequired),
    upserts,
    deletedIds,
  };
}

// バッチ分割時のページカーソル。IndexedDB に保存されても再開できるよう自己完結させる。
const BATCH_PAGE_CURSOR_PREFIX = 'fe1:';

type BatchPageCursor = { i: number; c: string | null; s: number | null };

const encodeBatchPageCursor = (state: BatchPageCursor): string =>
  BATCH_PAGE_CURSOR_PREFIX + encodeURIComponent(JSON.stringify(state));

const decodeBatchPageCursor = (
  value: string | null | undefined,
  batchCount: number
): BatchPageCursor => {
  const fromStart: BatchPageCursor = { i: 0, c: null, s: null };
  if (!value) return fromStart;
  // 分割前のバージョンで保存された通常カーソルなど、解釈できないカーソルは
  // エラーにせず先頭から取得し直す（結果はIDでマージされるため重複しても問題ない）。
  if (!value.startsWith(BATCH_PAGE_CURSOR_PREFIX)) return fromStart;
  try {
    const parsed = JSON.parse(
      decodeURIComponent(value.slice(BATCH_PAGE_CURSOR_PREFIX.length))
    );
    const i = Number(parsed?.i);
    if (!Number.isInteger(i) || i < 0 || i >= batchCount) return fromStart;
    return {
      i,
      c: typeof parsed.c === 'string' && parsed.c ? parsed.c : null,
      s: Number.isSafeInteger(parsed.s) && parsed.s >= 0 ? parsed.s : null,
    };
  } catch {
    return fromStart;
  }
};

export async function fetchWeeklyReportCreatorPage(
  args: WeeklyReportCreatorFetchArgs,
  options: {
    pageSize: number;
    pageCursor?: string | null;
    query?: string;
  }
): Promise<WeeklyReportCreatorPage> {
  const batches = buildCreatorParamBatches(args);
  const isBatched = batches.length > 1;
  // 分割しない条件に、分割時のカーソル(fe1:)が残っていてもサーバーへは渡さない。
  const plainCursor =
    options.pageCursor && !options.pageCursor.startsWith(BATCH_PAGE_CURSOR_PREFIX)
      ? options.pageCursor
      : null;
  const state: BatchPageCursor = isBatched
    ? decodeBatchPageCursor(options.pageCursor, batches.length)
    : { i: 0, c: plainCursor, s: null };

  const params = batches[state.i];
  const query = String(options.query ?? '').trim();
  const pageSizeLimit = query ? 200 : 1000;
  params.set(
    'page_size',
    String(Math.max(1, Math.min(pageSizeLimit, Math.floor(options.pageSize))))
  );
  if (state.c) params.set('page_cursor', state.c);
  if (query) params.set('q', query);

  const resp = await apiClient.get<any>('/weekly-report-creators-page', {
    params,
    signal: args.signal,
  });
  const data = resp.data ?? {};
  const syncCursor = Number(data.sync_cursor);
  const innerNext =
    typeof data.next_page_cursor === 'string' && data.next_page_cursor
      ? data.next_page_cursor
      : null;
  const innerHasMore = Boolean(data.has_more);
  const pageSyncCursor =
    Number.isSafeInteger(syncCursor) && syncCursor >= 0 ? syncCursor : null;
  const items = normalizeCreatorOptions(data.items);

  if (!isBatched) {
    return {
      items,
      nextPageCursor: innerNext,
      hasMore: innerHasMore,
      syncCursor: pageSyncCursor,
    };
  }

  // 同期カーソルは最初のバッチのものを全ページで引き継ぐ（最も古く、取りこぼしがない）
  const batchSyncCursor = state.s ?? pageSyncCursor;
  let next: BatchPageCursor | null = null;
  if (innerHasMore && innerNext) {
    next = { i: state.i, c: innerNext, s: batchSyncCursor };
  } else if (state.i + 1 < batches.length) {
    next = { i: state.i + 1, c: null, s: batchSyncCursor };
  }
  return {
    items,
    nextPageCursor: next ? encodeBatchPageCursor(next) : null,
    hasMore: next != null,
    syncCursor: batchSyncCursor,
  };
}

export async function fetchWeeklyReportCreators(
  args: WeeklyReportCreatorFetchArgs
): Promise<WeeklyReportCreatorOption[]> {
  return (await fetchWeeklyReportCreatorSnapshot(args)).options;
}

type DepartmentScope = Array<string | number>;
type DateRange = { start: string; end: string };

export interface WeeklyAdaptiveFetchArgs {
  startDate: string;
  endDate: string;
  baseParams?: URLSearchParams;
  departmentScopes: DepartmentScope[];
  userDep?: string;
  userTeamId?: string | number;
  appendTemporaryFlg0?: boolean;
  splitThreshold?: number;
  initialChunkDays?: number;
  minChunkDays?: number;
  maxConcurrency?: number;
  outerMaxConcurrency?: number;
  signal?: AbortSignal;
}

export interface WeeklyAdaptiveFetchResult<T = any> {
  rows: T[];
  exact: boolean;
}

const parseYmdAsUtc = (ymd: string): Date => {
  const [y, m, d] = String(ymd).split('-').map((v) => Number(v));
  return new Date(Date.UTC(y, (m || 1) - 1, d || 1));
};

const toYmdFromUtc = (dt: Date): string => {
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, '0');
  const d = String(dt.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const addDaysUtc = (dt: Date, days: number): Date => {
  const next = new Date(dt);
  next.setUTCDate(next.getUTCDate() + days);
  return next;
};

const normalizeRange = (startDate: string, endDate: string): DateRange => {
  const s = parseYmdAsUtc(startDate);
  const e = parseYmdAsUtc(endDate);
  const [min, max] = s <= e ? [s, e] : [e, s];
  return { start: toYmdFromUtc(min), end: toYmdFromUtc(max) };
};

const rangeDaysInclusive = (range: DateRange): number => {
  const s = parseYmdAsUtc(range.start).getTime();
  const e = parseYmdAsUtc(range.end).getTime();
  const diff = Math.floor((e - s) / 86400000);
  return Math.max(diff + 1, 1);
};

const splitDateRangeInHalf = (range: DateRange): DateRange[] => {
  const totalDays = rangeDaysInclusive(range);
  if (totalDays <= 1) return [range];

  const start = parseYmdAsUtc(range.start);
  const leftDays = Math.floor(totalDays / 2);
  const leftEnd = addDaysUtc(start, Math.max(leftDays - 1, 0));
  const rightStart = addDaysUtc(leftEnd, 1);

  return [
    { start: range.start, end: toYmdFromUtc(leftEnd) },
    { start: toYmdFromUtc(rightStart), end: range.end },
  ];
};

const buildDateChunks = (
  startDate: string,
  endDate: string,
  chunkDays: number
): DateRange[] => {
  const normalized = normalizeRange(startDate, endDate);
  const from = parseYmdAsUtc(normalized.start);
  const to = parseYmdAsUtc(normalized.end);
  const size = Math.max(1, Math.trunc(chunkDays));
  const chunks: DateRange[] = [];

  let cursor = from;
  while (cursor <= to) {
    const chunkEndCandidate = addDaysUtc(cursor, size - 1);
    const chunkEnd = chunkEndCandidate <= to ? chunkEndCandidate : to;
    chunks.push({
      start: toYmdFromUtc(cursor),
      end: toYmdFromUtc(chunkEnd),
    });
    cursor = addDaysUtc(chunkEnd, 1);
  }

  return chunks;
};

const normalizeDepartmentScope = (scope: DepartmentScope | undefined): string[] =>
  Array.from(
    new Set(
      (scope ?? [])
        .map((v) => String(v).trim())
        .filter((v) => v.length > 0)
    )
  ).sort();

const buildAdaptiveSearchParams = (
  args: WeeklyAdaptiveFetchArgs,
  range: DateRange,
  scope: string[]
): URLSearchParams => {
  if (!args.baseParams) {
    const params = buildWeeklySearchParams({
      startDate: range.start,
      endDate: range.end,
      selectedDepartmentIds: scope,
      appendTemporaryFlg0: Boolean(args.appendTemporaryFlg0),
    });
    if (args.userDep) {
      params.set('USER_DEP', args.userDep);
    }
    if (args.userTeamId) {
      params.set('USER_TEAM_ID', String(args.userTeamId));
    }
    return params;
  }

  const params = new URLSearchParams(args.baseParams);
  params.delete('start_date');
  params.delete('end_date');
  params.delete('departments');
  params.delete('USER_DEP');
  params.delete('USER_TEAM_ID');

  params.set('start_date', range.start);
  params.set('end_date', range.end);
  scope.forEach((id) => params.append('departments', id));

  if (args.appendTemporaryFlg0) {
    params.set('temporary_flg', '0');
  }
  if (args.userDep) {
    params.set('USER_DEP', args.userDep);
  }
  if (args.userTeamId) {
    params.set('USER_TEAM_ID', String(args.userTeamId));
  }

  return params;
};

// 複数値の検索条件（発行者・チーム・課・週報分類）は、BE側ではいずれも IN / OR 条件のため、
// 値を複数リクエストへ分割して結果を和集合にしても元の検索と同じ結果になる。
// 全選択などで値が増えるとクエリがWAFのクエリ長上限(2KB)を超えて拒否されるため、
// 上限に収まるバッチへ分割して送る。複数グループが長い場合はバッチの直積を送る。
const SPLITTABLE_QUERY_KEY_GROUPS: string[][] = [
  ['create_by_user_ids', 'create_by', 'legacy_create_by'], // 発行者（互いにOR結合）
  ['teams'],
  ['departments'],
  ['report_types'],
];
const MAX_QUERY_LENGTH = 1800;
const BATCH_FETCH_CONCURRENCY = 4;

type QueryEntry = [string, string];

const queryEntryLength = ([key, value]: QueryEntry): number =>
  new URLSearchParams([[key, value]]).toString().length + 1;

const packEntries = (entries: QueryEntry[], budget: number): QueryEntry[][] => {
  const chunks: QueryEntry[][] = [];
  let current: QueryEntry[] = [];
  let currentLength = 0;
  entries.forEach((entry) => {
    const len = queryEntryLength(entry);
    if (current.length > 0 && currentLength + len > budget) {
      chunks.push(current);
      current = [];
      currentLength = 0;
    }
    current.push(entry);
    currentLength += len;
  });
  if (current.length > 0) chunks.push(current);
  return chunks;
};

export const splitParamsByQueryLength = (
  params: URLSearchParams,
  maxLength: number = MAX_QUERY_LENGTH
): URLSearchParams[] => {
  if (params.toString().length <= maxLength) return [new URLSearchParams(params)];

  const base = new URLSearchParams();
  const groups: QueryEntry[][] = SPLITTABLE_QUERY_KEY_GROUPS.map(() => []);
  params.forEach((value, key) => {
    const idx = SPLITTABLE_QUERY_KEY_GROUPS.findIndex((keys) => keys.includes(key));
    if (idx >= 0) groups[idx].push([key, value]);
    else base.append(key, value);
  });

  // 短いグループは分割せず、残りの予算を長いグループへ配分する
  let remaining = Math.max(maxLength - base.toString().length, Math.floor(maxLength / 2));
  const sizes = groups.map((g) => g.reduce((sum, e) => sum + queryEntryLength(e), 0));
  const order = groups
    .map((_, i) => i)
    .filter((i) => groups[i].length > 0)
    .sort((a, b) => sizes[a] - sizes[b]);
  const chunksByGroup: QueryEntry[][][] = groups.map(() => []);
  order.forEach((groupIdx, pos) => {
    const share = Math.floor(remaining / (order.length - pos));
    const budget = sizes[groupIdx] <= share ? sizes[groupIdx] : share;
    chunksByGroup[groupIdx] = packEntries(groups[groupIdx], budget);
    remaining -= Math.min(sizes[groupIdx], budget);
  });

  let batches: URLSearchParams[] = [new URLSearchParams(base)];
  chunksByGroup.forEach((chunks) => {
    if (chunks.length === 0) return;
    batches = batches.flatMap((batch) =>
      chunks.map((chunk) => {
        const next = new URLSearchParams(batch);
        chunk.forEach(([key, value]) => next.append(key, value));
        return next;
      })
    );
  });
  return batches;
};

/**
 * クエリ長が上限を超える検索条件を、バッチに分割して取得し結果を統合する。
 * 上限以内なら fetchWeeklyReports と同じ1リクエスト。
 */
export const fetchWeeklyReportsInBatches = async <T = any>(
  params: URLSearchParams,
  signal?: AbortSignal
): Promise<T[]> => {
  const batches = splitParamsByQueryLength(params);
  if (batches.length === 1) return fetchWeeklyReports<T>(batches[0], signal);
  const settled = await runWithConcurrency(
    batches.map((batch) => () =>
      withBatchSlot(() => fetchWeeklyReports<T>(batch, signal))
    ),
    BATCH_FETCH_CONCURRENCY
  );
  const results: T[][] = [];
  for (const item of settled) {
    // 一部のバッチが失敗した結果を完全な結果として返さない
    if (item.status === 'rejected') throw item.reason;
    results.push(item.value);
  }
  return dedupeRowsByWrId(results.flat());
};

const runWithConcurrency = async <T>(
  tasks: Array<() => Promise<T>>,
  concurrency: number
): Promise<Array<PromiseSettledResult<T>>> => {
  if (tasks.length === 0) return [];

  const limit = Math.max(1, Math.trunc(concurrency));
  const settled: Array<PromiseSettledResult<T>> = new Array(tasks.length);
  let cursor = 0;

  const workers = Array.from({ length: Math.min(limit, tasks.length) }, async () => {
    while (true) {
      const idx = cursor++;
      if (idx >= tasks.length) return;
      try {
        const value = await tasks[idx]();
        settled[idx] = { status: 'fulfilled', value };
      } catch (reason) {
        settled[idx] = { status: 'rejected', reason };
      }
    }
  });

  await Promise.all(workers);
  return settled;
};

const dedupeRowsByWrId = <T>(rows: T[]): T[] => {
  const seen = new Set<string>();
  const out: T[] = [];

  rows.forEach((row) => {
    const wrId =
      row && typeof row === 'object' ? (row as any).wr_id : undefined;
    if (wrId == null || String(wrId).trim() === '') {
      out.push(row);
      return;
    }
    const key = String(wrId);
    if (seen.has(key)) return;
    seen.add(key);
    out.push(row);
  });

  return out;
};

const fetchWeeklyReportsBySingleScopeAdaptively = async <T = any>(
  args: WeeklyAdaptiveFetchArgs,
  scope: string[]
): Promise<WeeklyAdaptiveFetchResult<T>> => {
  const splitAt = Math.max(0, Math.trunc(args.splitThreshold ?? 100));
  const minChunkDays = Math.max(1, Math.trunc(args.minChunkDays ?? 1));
  const initialChunkDays = Math.max(1, Math.trunc(args.initialChunkDays ?? 31));
  const maxConcurrency = Math.max(1, Math.trunc(args.maxConcurrency ?? 4));

  let exact = true;
  const rows: T[] = [];
  let splitRanges = buildDateChunks(args.startDate, args.endDate, initialChunkDays);

  const shouldSplitRange = (range: DateRange, count: number): boolean => {
    if (splitAt <= 0) return false;
    if (count < splitAt) return false;
    return rangeDaysInclusive(range) > minChunkDays;
  };

  const isSaturatedRange = (count: number): boolean => {
    if (splitAt <= 0) return false;
    return count >= splitAt;
  };

  while (splitRanges.length > 0) {
    const currentRanges = splitRanges;
    splitRanges = [];

    const settled = await runWithConcurrency(
      currentRanges.map((range) => async () => {
        const params = buildAdaptiveSearchParams(args, range, scope);

        const fetchedRows = await fetchWeeklyReportsInBatches<T>(params, args.signal);
        if (shouldSplitRange(range, fetchedRows.length)) {
          return {
            rows: [] as T[],
            nextRanges: splitDateRangeInHalf(range),
            saturated: false,
          };
        }

        return {
          rows: fetchedRows,
          nextRanges: [] as DateRange[],
          saturated: isSaturatedRange(fetchedRows.length),
        };
      }),
      maxConcurrency
    );

    for (const item of settled) {
      if (item.status === 'rejected') {
        // 一部だけの結果を完全な結果として返さない（0件表示で失敗を隠さない）
        throw item.reason;
      }

      rows.push(...item.value.rows);
      if (item.value.nextRanges.length > 0) {
        splitRanges.push(...item.value.nextRanges);
      }
      if (item.value.saturated) {
        exact = false;
      }
    }
  }

  return { rows: dedupeRowsByWrId(rows), exact };
};

export async function fetchWeeklyReportsByDepartmentScopesAdaptively<T = any>(
  args: WeeklyAdaptiveFetchArgs
): Promise<WeeklyAdaptiveFetchResult<T>> {
  const normalized = normalizeRange(args.startDate, args.endDate);
  const scopesRaw = args.departmentScopes && args.departmentScopes.length > 0
    ? args.departmentScopes
    : [[]];
  const scopes = scopesRaw.map((s) => normalizeDepartmentScope(s));
  const outerMaxConcurrency = Math.max(1, Math.trunc(args.outerMaxConcurrency ?? 1));

  const settled = await runWithConcurrency(
    scopes.map((scope) => () =>
      fetchWeeklyReportsBySingleScopeAdaptively<T>(
        {
          ...args,
          startDate: normalized.start,
          endDate: normalized.end,
        },
        scope
      )
    ),
    outerMaxConcurrency
  );

  let exact = true;
  const rows: T[] = [];

  settled.forEach((item) => {
    if (item.status === 'rejected') {
      // 一部の課スコープだけの結果を完全な結果として返さない（0件表示で失敗を隠さない）
      throw item.reason;
    }

    rows.push(...item.value.rows);
    if (!item.value.exact) {
      exact = false;
    }
  });

  return {
    rows: dedupeRowsByWrId(rows),
    exact,
  };
}
